// components/SuperAdminPanel.js - Panneau Super Admin pour Gestion des Entreprises Client
import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';
import { useTranslation } from '../i18n/LanguageContext';
import CustomSelect from './CustomSelect';
import {
  Building, Users, Clock, CheckCircle, XCircle, AlertCircle, RefreshCw, 
  Search, Filter, Home, Mail, Phone, MapPin, Calendar, DollarSign,
  Shield, Eye, Ban, UserCheck, TrendingUp, FileText, CreditCard
} from 'lucide-react';

const SuperAdminPanel = ({ onNavigate }) => {
  const { t, language } = useTranslation();
  const dateLocale = language === 'fr' ? 'fr-FR' : 'en-GB';
  const [activeTab, setActiveTab] = useState('inscriptions');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  // États pour les demandes d'inscription
  const [registrationRequests, setRegistrationRequests] = useState([]);
  const [filteredRegistrations, setFilteredRegistrations] = useState([]);
  const [searchRegistrations, setSearchRegistrations] = useState('');

  // États pour les entreprises
  const [companies, setCompanies] = useState([]);
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [searchCompanies, setSearchCompanies] = useState('');
  const [filterStatusCompanies, setFilterStatusCompanies] = useState('all');
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [showCompanyModal, setShowCompanyModal] = useState(false);

  // États pour les abonnements
  const [subscriptions, setSubscriptions] = useState([]);
  const [filteredSubscriptions, setFilteredSubscriptions] = useState([]);
  const [searchSubscriptions, setSearchSubscriptions] = useState('');
  const [filterPlanSubscriptions, setFilterPlanSubscriptions] = useState('all');
  const [filterStatusSubscriptions, setFilterStatusSubscriptions] = useState('all');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [showCreateCompanyModal, setShowCreateCompanyModal] = useState(false);
  const [createCompanyPlan, setCreateCompanyPlan] = useState('starter');

  // États pour les statistiques
  const [stats, setStats] = useState({
    totalCompanies: 0,
    activeCompanies: 0,
    pendingRequests: 0,
    trialCompanies: 0,
    totalEmployees: 0
  });

  useEffect(() => {
    console.log('🛡️ SuperAdminPanel monté - Chargement des données...');
    loadAllData();
    const interval = setInterval(loadAllData, 30000); // Actualiser toutes les 30s
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    filterRegistrations();
  }, [searchRegistrations, registrationRequests]);

  useEffect(() => {
    filterCompanies();
  }, [searchCompanies, filterStatusCompanies, companies]);

  useEffect(() => {
    filterSubscriptions();
  }, [searchSubscriptions, filterPlanSubscriptions, filterStatusSubscriptions, subscriptions]);

  // Refetch stats when switching to Statistics tab - always show fresh run-time data
  useEffect(() => {
    if (activeTab === 'statistiques') {
      loadStats();
    }
  }, [activeTab]);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const loadAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadRegistrationRequests(),
        loadCompanies(),
        loadSubscriptions(),
        loadStats()
      ]);
    } catch (error) {
      console.error('Erreur chargement données:', error);
      setMessage(t('super_admin.error_loading_data_msg'));
    } finally {
      setLoading(false);
    }
  };

  // Charger les statistiques globales
  const loadStats = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/stats`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data.stats || {
          totalCompanies: 0,
          activeCompanies: 0,
          pendingRequests: 0,
          trialCompanies: 0,
          totalEmployees: 0
        });
      }
    } catch (error) {
      console.error('Erreur chargement stats:', error);
    }
  };

  // Charger les demandes d'inscription
  const loadRegistrationRequests = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/registration-requests`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setRegistrationRequests(data.requests || []);
        setFilteredRegistrations(data.requests || []);
      }
    } catch (error) {
      console.error('Erreur chargement demandes inscription:', error);
    }
  };

  // Charger toutes les entreprises
  const loadCompanies = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/companies`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.companies || []);
        setFilteredCompanies(data.companies || []);
      }
    } catch (error) {
      console.error('Erreur chargement entreprises:', error);
    }
  };

  // Charger les abonnements (entreprises avec info Stripe)
  const loadSubscriptions = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/companies`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        // Filtrer pour avoir seulement les entreprises avec info abonnement
        const companiesWithSubscriptions = (data.companies || []).map(async (company) => {
          // Récupérer les détails d'abonnement si disponible
          if (company.stripe_customer_id || company.stripe_subscription_id) {
            try {
              const subResponse = await fetch(`${API_BASE}/api/super-admin/companies/${company.id}/subscription`, {
                headers: getAuthHeaders()
              });
              if (subResponse.ok) {
                const subData = await subResponse.json();
                return { ...company, subscriptionDetails: subData };
              }
            } catch (error) {
              console.error('Erreur chargement détails abonnement:', error);
            }
          }
          return company;
        });
        const resolved = await Promise.all(companiesWithSubscriptions);
        setSubscriptions(resolved);
        setFilteredSubscriptions(resolved);
      }
    } catch (error) {
      console.error('Erreur chargement abonnements:', error);
    }
  };

  // Filtrer les abonnements
  const filterSubscriptions = () => {
    let filtered = subscriptions;
    
    // Filtre par recherche
    if (searchSubscriptions) {
      filtered = filtered.filter(sub => 
        sub.name?.toLowerCase().includes(searchSubscriptions.toLowerCase())
      );
    }
    
    // Filtre par plan
    if (filterPlanSubscriptions !== 'all') {
      filtered = filtered.filter(sub => 
        sub.subscription_plan === filterPlanSubscriptions
      );
    }
    
    // Filtre par statut
    if (filterStatusSubscriptions !== 'all') {
      filtered = filtered.filter(sub => {
        if (filterStatusSubscriptions === 'trial') {
          return sub.subscription_status === 'trial' || 
                 (sub.trial_ends_at && new Date(sub.trial_ends_at) > new Date());
        }
        return sub.subscription_status === filterStatusSubscriptions;
      });
    }
    
    setFilteredSubscriptions(filtered);
  };

  // Approuver une demande d'inscription
  const approveRegistration = async (requestId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir approuver cette demande d\'inscription ?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/approve-registration/${requestId}`, {
        method: 'PUT',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setMessage(`✅ Entreprise approuvée ! Mot de passe temporaire: ${data.tempPassword || 'N/A'}`);
        await loadAllData();
        setTimeout(() => setMessage(''), 10000);
      } else {
        const errorData = await response.json();
        setMessage(`❌ ${errorData.error || t('super_admin.error_approve')}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Erreur approbation:', error);
      setMessage(`❌ ${t('super_admin.error_network')}: ${error.message || t('super_admin.error_contact_server')}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  // Rejeter une demande d'inscription
  const rejectRegistration = async (requestId, reason) => {
    if (!window.confirm('Êtes-vous sûr de vouloir rejeter cette demande d\'inscription ?')) {
      return;
    }
    
    const rejectionReason = reason || prompt('Raison du rejet (obligatoire):') || '';
    if (!rejectionReason || rejectionReason.trim() === '') {
      setMessage('❌ La raison du rejet est obligatoire');
      setTimeout(() => setMessage(''), 3000);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/super-admin/reject-registration/${requestId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ reason: rejectionReason.trim() })
      });
      if (response.ok) {
        setMessage('✅ Demande rejetée avec succès');
        await loadAllData();
        setTimeout(() => setMessage(''), 5000);
      } else {
        const errorData = await response.json();
        setMessage(`❌ ${errorData.error || t('super_admin.error_reject')}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Erreur rejet:', error);
      setMessage(`❌ ${t('super_admin.error_network')}: ${error.message || t('super_admin.error_contact_server')}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  // Activer/Désactiver une entreprise
  const expireTrial = async (companyId, companyName, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(t('super_admin.expire_trial_test') + ` — ${companyName || 'this company'}? This will make the subscription appear expired for testing.`)) return;
    setMessage('');
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/expire-trial`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ companyId })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessage(`❌ ${data.error || t('super_admin.error_loading_data_msg')}`);
        return;
      }
      setMessage(`✅ ${data.message || 'Trial expired.'}`);
      loadCompanies();
      loadStats();
      loadSubscriptions();
    } catch (err) {
      setMessage(`❌ ${t('super_admin.error_contact_server')}: ${err.message || ''}`);
    }
  };

  const resetTrial = async (companyId, companyName, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm(t('super_admin.reset_trial_test') + ` — ${companyName || 'this company'}? Trial will be 14 days from now.`)) return;
    setMessage('');
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/reset-trial`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ companyId })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setMessage(`❌ ${data.error || t('super_admin.error_loading_data_msg')}`);
        return;
      }
      setMessage(`✅ ${data.message || 'Trial reset.'}`);
      loadCompanies();
      loadStats();
      loadSubscriptions();
    } catch (err) {
      setMessage(`❌ ${t('super_admin.error_contact_server')}: ${err.message || ''}`);
    }
  };

  const toggleCompanyStatus = async (companyId, currentStatus, companyName) => {
    const action = currentStatus ? 'suspendre' : 'activer';
    if (!window.confirm(`Êtes-vous sûr de vouloir ${action} l'entreprise "${companyName || 'cette entreprise'}" ?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/companies/${companyId}/toggle-status`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ is_active: !currentStatus })
      });
      if (response.ok) {
        const data = await response.json();
        setMessage(`✅ ${data.message || `Entreprise ${!currentStatus ? 'activée' : 'suspendue'} avec succès`}`);
        await loadCompanies();
        setTimeout(() => setMessage(''), 5000);
      } else {
        const errorData = await response.json();
        setMessage(`❌ ${errorData.error || t('super_admin.error_toggle_status')}`);
        setTimeout(() => setMessage(''), 5000);
      }
    } catch (error) {
      console.error('Erreur toggle status:', error);
      setMessage(`❌ ${t('super_admin.error_network')}: ${error.message || t('super_admin.error_contact_server')}`);
      setTimeout(() => setMessage(''), 5000);
    }
  };

  // Filtrer les demandes d'inscription
  const filterRegistrations = () => {
    let filtered = registrationRequests;
    if (searchRegistrations) {
      filtered = filtered.filter(req => 
        req.company_name?.toLowerCase().includes(searchRegistrations.toLowerCase()) ||
        req.admin_email?.toLowerCase().includes(searchRegistrations.toLowerCase())
      );
    }
    setFilteredRegistrations(filtered);
  };

  // Filtrer les entreprises
  const filterCompanies = () => {
    let filtered = companies;
    if (filterStatusCompanies !== 'all') {
      filtered = filtered.filter(comp => {
        if (filterStatusCompanies === 'active') return comp.status === 'active' || comp.status === 'approved';
        if (filterStatusCompanies === 'trial') return comp.trial_expires_at && new Date(comp.trial_expires_at) > new Date();
        if (filterStatusCompanies === 'suspended') return comp.status === 'suspended';
        return comp.status === filterStatusCompanies;
      });
    }
    if (searchCompanies) {
      filtered = filtered.filter(comp => 
        comp.name?.toLowerCase().includes(searchCompanies.toLowerCase()) ||
        comp.email?.toLowerCase().includes(searchCompanies.toLowerCase())
      );
    }
    setFilteredCompanies(filtered);
  };

  const tabs = [
    { id: 'inscriptions', label: t('super_admin.tab_registrations'), icon: <FileText size={20} />, count: registrationRequests.filter(r => r.status === 'pending').length },
    { id: 'entreprises', label: t('super_admin.tab_companies'), icon: <Building size={20} />, count: companies.length },
    { id: 'abonnements', label: t('super_admin.tab_subscriptions'), icon: <CreditCard size={20} />, count: subscriptions.filter(s => s.subscription_status === 'active' || s.subscription_status === 'trial').length },
    { id: 'statistiques', label: t('super_admin.tab_statistics'), icon: <TrendingUp size={20} /> }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
      color: '#ffffff',
      fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", sans-serif',
      paddingTop: '80px'
    }}>
      {/* Header */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        padding: '20px 40px',
        background: 'rgba(10, 14, 39, 0.95)',
        backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '20px'
      }}>
        <button
          onClick={() => onNavigate && onNavigate('home')}
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#ffffff',
            padding: '10px 16px',
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          <Home size={18} />
          {t('super_admin.home')}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <Shield size={24} color="#dc2626" />
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>{t('super_admin.title')}</h1>
        </div>
        <button
          onClick={loadAllData}
          style={{
            background: 'linear-gradient(135deg, #dc2626, #ef4444)',
            border: 'none',
            color: '#ffffff',
            padding: '10px 16px',
            borderRadius: '12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            fontWeight: 600
          }}
        >
          <RefreshCw size={18} />
          {t('super_admin.refresh') || 'Actualiser'}
        </button>
        {/* Fix Peak Performance Users button removed */}
      </div>

      {/* Message */}
      {message && (
        <div style={{
          position: 'fixed',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: message.includes('❌') ? 'rgba(239, 68, 68, 0.9)' : 'rgba(16, 185, 129, 0.9)',
          color: '#ffffff',
          padding: '12px 24px',
          borderRadius: '12px',
          zIndex: 999,
          fontSize: '14px',
          fontWeight: 600,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          maxWidth: '90%',
          textAlign: 'center'
        }}
        onClick={() => setMessage('')}
        >
          {message}
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex',
        gap: '12px',
        padding: '20px 40px',
        borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        overflowX: 'auto'
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              background: activeTab === tab.id 
                ? 'linear-gradient(135deg, #dc2626, #ef4444)' 
                : 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#ffffff',
              padding: '12px 20px',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
              transition: 'all 0.3s'
            }}
          >
            {tab.icon}
            {tab.label}
            {tab.count > 0 && (
              <span style={{
                background: 'rgba(239, 68, 68, 0.8)',
                color: '#ffffff',
                padding: '2px 8px',
                borderRadius: '10px',
                fontSize: '12px',
                fontWeight: 700,
                marginLeft: '4px'
              }}>
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={{ padding: '40px', maxWidth: '1600px', margin: '0 auto' }}>
        {loading && (
          <div style={{ textAlign: 'center', padding: '60px', color: 'rgba(255, 255, 255, 0.7)' }}>
            {t('super_admin.loading')}
          </div>
        )}

        {/* Onglet Statistiques */}
        {activeTab === 'statistiques' && !loading && (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '24px',
            marginBottom: '40px'
          }}>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <Building size={32} style={{ marginBottom: '12px', color: '#00d4ff' }} />
              <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px' }}>
                {stats.totalCompanies}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                {t('super_admin.stat_total_companies')}
              </div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <CheckCircle size={32} style={{ marginBottom: '12px', color: '#10b981' }} />
              <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px' }}>
                {stats.activeCompanies}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                {t('super_admin.stat_active_companies')}
              </div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <Clock size={32} style={{ marginBottom: '12px', color: '#ffc107' }} />
              <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px' }}>
                {stats.pendingRequests}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                {t('super_admin.stat_pending_requests')}
              </div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <Calendar size={32} style={{ marginBottom: '12px', color: '#8b5cf6' }} />
              <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px' }}>
                {stats.trialCompanies}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                {t('super_admin.stat_trial_companies')}
              </div>
            </div>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              borderRadius: '20px',
              padding: '24px',
              textAlign: 'center'
            }}>
              <Users size={32} style={{ marginBottom: '12px', color: '#e91e63' }} />
              <div style={{ fontSize: '32px', fontWeight: 700, marginBottom: '8px' }}>
                {stats.totalEmployees}
              </div>
              <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '14px' }}>
                {t('super_admin.stat_total_employees')}
              </div>
            </div>
          </div>
        )}

        {/* Onglet Demandes d'Inscription */}
        {activeTab === 'inscriptions' && !loading && (
          <div>
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{
                flex: 1,
                minWidth: '300px',
                position: 'relative'
              }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }} />
                <input
                  type="text"
                  placeholder={t('super_admin.search_registrations')}
                  value={searchRegistrations}
                  onChange={(e) => setSearchRegistrations(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {filteredRegistrations.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '20px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                {t('super_admin.no_registrations')}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gap: '20px'
              }}>
                {filteredRegistrations.map(request => (
                  <div
                    key={request.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '20px',
                      padding: '24px',
                      transition: 'all 0.3s'
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px',
                      flexWrap: 'wrap',
                      gap: '16px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px',
                          marginBottom: '12px'
                        }}>
                          <h3 style={{
                            fontSize: '20px',
                            fontWeight: 700,
                            margin: 0,
                            color: '#ffffff'
                          }}>
                            {request.company_name}
                          </h3>
                          <span style={{
                            background: request.status === 'pending' 
                              ? 'rgba(255, 193, 7, 0.2)' 
                              : request.status === 'approved'
                              ? 'rgba(16, 185, 129, 0.2)'
                              : 'rgba(239, 68, 68, 0.2)',
                            color: request.status === 'pending'
                              ? '#ffc107'
                              : request.status === 'approved'
                              ? '#10b981'
                              : '#ef4444',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase'
                          }}>
                            {request.status === 'pending' ? t('super_admin.status_pending') : request.status === 'approved' ? t('super_admin.status_approved') : t('super_admin.status_rejected')}
                          </span>
                        </div>
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                          gap: '12px',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '14px'
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Mail size={16} />
                            {request.admin_email}
                          </div>
                          {request.phone && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Phone size={16} />
                              {request.phone}
                            </div>
                          )}
                          {request.company_size && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <Users size={16} />
                              {request.company_size} {t('super_admin.employees_count')}
                            </div>
                          )}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Calendar size={16} />
                            {new Date(request.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </div>
                        {request.message && (
                          <div style={{
                            marginTop: '12px',
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px',
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontSize: '14px'
                          }}>
                            <strong>{t('super_admin.message')}:</strong> {request.message}
                          </div>
                        )}
                      </div>
                      {request.status === 'pending' && (
                        <div style={{
                          display: 'flex',
                          gap: '12px',
                          flexWrap: 'wrap'
                        }}>
                          <button
                            onClick={() => approveRegistration(request.id)}
                            style={{
                              background: 'linear-gradient(135deg, #10b981, #059669)',
                              border: 'none',
                              color: '#ffffff',
                              padding: '10px 20px',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            <CheckCircle size={18} />
                            {t('super_admin.approve')}
                          </button>
                          <button
                            onClick={() => rejectRegistration(request.id)}
                            style={{
                              background: 'rgba(239, 68, 68, 0.8)',
                              border: 'none',
                              color: '#ffffff',
                              padding: '10px 20px',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '14px',
                              fontWeight: 600
                            }}
                          >
                            <XCircle size={18} />
                            {t('super_admin.reject')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Onglet Entreprises */}
        {activeTab === 'entreprises' && !loading && (
          <div>
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{
                flex: 1,
                minWidth: '300px',
                position: 'relative'
              }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }} />
                <input
                  type="text"
                  placeholder={t('super_admin.search_companies')}
                  value={searchCompanies}
                  onChange={(e) => setSearchCompanies(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <CustomSelect
                name="filterStatusCompanies"
                value={filterStatusCompanies}
                onChange={(val) => setFilterStatusCompanies(val)}
                placeholder={t('super_admin.all_statuses')}
                options={[
                  { value: 'all', label: t('super_admin.all_statuses') },
                  { value: 'active', label: t('super_admin.filter_active') },
                  { value: 'trial', label: t('super_admin.filter_trial') },
                  { value: 'suspended', label: t('super_admin.filter_suspended') }
                ]}
                style={{
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  minWidth: '160px'
                }}
              />
            </div>

            {filteredCompanies.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '20px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                {t('super_admin.no_companies')}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                gap: '20px'
              }}>
                {filteredCompanies.map(company => (
                  <div
                    key={company.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '20px',
                      padding: '24px',
                      cursor: 'pointer',
                      transition: 'all 0.3s'
                    }}
                    onClick={() => {
                      setSelectedCompany(company);
                      setShowCompanyModal(true);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(220, 38, 38, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: 700,
                          marginBottom: '8px',
                          color: '#ffffff'
                        }}>
                          {company.name}
                        </h3>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '14px'
                        }}>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: company.status === 'active' || company.status === 'approved'
                              ? 'rgba(16, 185, 129, 0.2)' 
                              : company.trial_expires_at && new Date(company.trial_expires_at) > new Date()
                              ? 'rgba(255, 193, 7, 0.2)'
                              : 'rgba(239, 68, 68, 0.2)',
                            color: company.status === 'active' || company.status === 'approved'
                              ? '#10b981'
                              : company.trial_expires_at && new Date(company.trial_expires_at) > new Date()
                              ? '#ffc107'
                              : '#ef4444',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            width: 'fit-content'
                          }}>
                            {company.trial_expires_at && new Date(company.trial_expires_at) > new Date() 
                              ? t('super_admin.trial_short') 
                              : company.status === 'active' || company.status === 'approved'
                              ? t('super_admin.status_active')
                              : company.status || t('super_admin.status_unknown')}
                          </div>
                          {company.trial_expires_at && (
                            <div>
                              <strong>{t('super_admin.trial_until')}:</strong> {new Date(company.trial_expires_at).toLocaleDateString(dateLocale)}
                            </div>
                          )}
                          <div>
                            <strong>{t('super_admin.created_on')}:</strong> {new Date(company.created_at).toLocaleDateString(dateLocale)}
                          </div>
                          {company.user_count !== undefined && (
                            <div>
                              <strong>{t('super_admin.users_count') || 'Utilisateurs'}:</strong> {company.user_count}
                            </div>
                          )}
                          {company.employee_count !== undefined && (
                            <div>
                              <strong>{t('super_admin.employees_count') || 'Employés'}:</strong> {company.employee_count}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}
                    onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => {
                          setSelectedCompany(company);
                          setShowCompanyModal(true);
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#ffffff',
                          padding: '8px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Eye size={16} />
                        {t('super_admin.view_details')}
                      </button>
                      <button
                        onClick={() => toggleCompanyStatus(company.id, company.status === 'active' || company.status === 'approved', company.name)}
                        style={{
                          background: (company.status === 'active' || company.status === 'approved')
                            ? 'rgba(239, 68, 68, 0.8)' 
                            : 'rgba(16, 185, 129, 0.8)',
                          border: 'none',
                          color: '#ffffff',
                          padding: '8px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        {(company.status === 'active' || company.status === 'approved') ? <Ban size={16} /> : <UserCheck size={16} />}
                        {(company.status === 'active' || company.status === 'approved') ? t('super_admin.suspend') : t('super_admin.activate')}
                      </button>
                      {company.subscription_status === 'trial' && (
                        <>
                          <button
                            onClick={(e) => expireTrial(company.id, company.name, e)}
                            style={{
                              background: 'rgba(245, 158, 11, 0.8)',
                              border: 'none',
                              color: '#ffffff',
                              padding: '8px 16px',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            title="Set trial to expired so you can test the subscription-expired → renew flow"
                          >
                            <Clock size={16} />
                            {t('super_admin.expire_trial_test')}
                          </button>
                          <button
                            onClick={(e) => resetTrial(company.id, company.name, e)}
                            style={{
                              background: 'rgba(16, 185, 129, 0.8)',
                              border: 'none',
                              color: '#ffffff',
                              padding: '8px 16px',
                              borderRadius: '10px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: 600,
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                            title="Reset trial to 14 days from now (testing)"
                          >
                            <RefreshCw size={16} />
                            {t('super_admin.reset_trial_test')}
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Onglet Abonnements */}
        {activeTab === 'abonnements' && !loading && (
          <div>
            <div style={{
              display: 'flex',
              gap: '16px',
              marginBottom: '24px',
              flexWrap: 'wrap',
              alignItems: 'center'
            }}>
              <div style={{
                flex: 1,
                minWidth: '300px',
                position: 'relative'
              }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'rgba(255, 255, 255, 0.5)'
                }} />
                <input
                  type="text"
                  placeholder={t('super_admin.search_subscriptions')}
                  value={searchSubscriptions}
                  onChange={(e) => setSearchSubscriptions(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <CustomSelect
                name="filterPlanSubscriptions"
                value={filterPlanSubscriptions}
                onChange={(val) => setFilterPlanSubscriptions(val)}
                placeholder={t('super_admin.all_plans')}
                options={[
                  { value: 'all', label: t('super_admin.all_plans') },
                  { value: 'starter', label: t('super_admin.plan_starter') },
                  { value: 'business', label: t('super_admin.plan_business') },
                  { value: 'enterprise', label: t('super_admin.plan_enterprise') }
                ]}
                style={{
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  minWidth: '160px'
                }}
              />
              <CustomSelect
                name="filterStatusSubscriptions"
                value={filterStatusSubscriptions}
                onChange={(val) => setFilterStatusSubscriptions(val)}
                placeholder={t('super_admin.all_statuses')}
                options={[
                  { value: 'all', label: t('super_admin.all_statuses') },
                  { value: 'trial', label: t('super_admin.filter_trial') },
                  { value: 'active', label: t('super_admin.status_active') },
                  { value: 'suspended', label: t('super_admin.status_suspended') },
                  { value: 'cancelled', label: t('super_admin.status_cancelled') }
                ]}
                style={{
                  padding: '12px 16px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer',
                  minWidth: '160px'
                }}
              />
              <button
                onClick={() => setShowCreateCompanyModal(true)}
                style={{
                  background: 'linear-gradient(135deg, #dc2626, #ef4444)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '12px 20px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <Building size={18} />
                {t('super_admin.create_company')}
              </button>
            </div>

            {filteredSubscriptions.length === 0 ? (
              <div style={{
                textAlign: 'center',
                padding: '60px',
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '20px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                {t('super_admin.no_subscriptions')}
              </div>
            ) : (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(400px, 1fr))',
                gap: '20px'
              }}>
                {filteredSubscriptions.map(company => (
                  <div
                    key={company.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '20px',
                      padding: '24px',
                      cursor: 'pointer',
                      transition: 'all 0.3s'
                    }}
                    onClick={() => {
                      setSelectedSubscription(company);
                      setShowSubscriptionModal(true);
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(220, 38, 38, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      marginBottom: '16px'
                    }}>
                      <div style={{ flex: 1 }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: 700,
                          marginBottom: '8px',
                          color: '#ffffff'
                        }}>
                          {company.name}
                        </h3>
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          color: 'rgba(255, 255, 255, 0.7)',
                          fontSize: '14px'
                        }}>
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: company.subscription_status === 'active'
                              ? 'rgba(16, 185, 129, 0.2)' 
                              : company.subscription_status === 'trial'
                              ? 'rgba(255, 193, 7, 0.2)'
                              : 'rgba(239, 68, 68, 0.2)',
                            color: company.subscription_status === 'active'
                              ? '#10b981'
                              : company.subscription_status === 'trial'
                              ? '#ffc107'
                              : '#ef4444',
                            padding: '4px 12px',
                            borderRadius: '8px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            width: 'fit-content'
                          }}>
                            {company.subscription_status || 'N/A'}
                          </div>
                          <div>
                            <strong>Plan:</strong> {company.subscription_plan || 'N/A'}
                          </div>
                          {company.current_period_end && (
                            <div>
                              <strong>Prochaine facturation:</strong> {new Date(company.current_period_end).toLocaleDateString('fr-FR')}
                            </div>
                          )}
                          {company.stripe_customer_id && (
                            <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.5)' }}>
                              <strong>Customer ID:</strong> {company.stripe_customer_id.substring(0, 20)}...
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '8px',
                      flexWrap: 'wrap'
                    }}
                    onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        onClick={() => {
                          setSelectedSubscription(company);
                          setShowSubscriptionModal(true);
                        }}
                        style={{
                          background: 'rgba(255, 255, 255, 0.1)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                          color: '#ffffff',
                          padding: '8px 16px',
                          borderRadius: '10px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Eye size={16} />
                        {t('super_admin.manage')}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Détails Entreprise */}
      {showCompanyModal && selectedCompany && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}
        onClick={() => setShowCompanyModal(false)}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#ffffff'
              }}>
                {t('super_admin.company_details')}
              </h2>
              <button
                onClick={() => setShowCompanyModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  color: '#ffffff',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '14px'
            }}>
              <div style={{
                background: 'rgba(255, 255, 255, 0.05)',
                padding: '16px',
                borderRadius: '12px',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <h3 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>{t('super_admin.general_info')}</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                  <div><strong>Nom:</strong> {selectedCompany.name}</div>
                  <div><strong>{t('super_admin.status_label')}:</strong> 
                    <span style={{
                      display: 'inline-block',
                      marginLeft: '8px',
                      padding: '4px 12px',
                      borderRadius: '8px',
                      fontSize: '12px',
                      fontWeight: 600,
                      background: selectedCompany.status === 'active' || selectedCompany.status === 'approved'
                        ? 'rgba(16, 185, 129, 0.2)'
                        : selectedCompany.status === 'suspended'
                        ? 'rgba(239, 68, 68, 0.2)'
                        : 'rgba(255, 193, 7, 0.2)',
                      color: selectedCompany.status === 'active' || selectedCompany.status === 'approved'
                        ? '#10b981'
                        : selectedCompany.status === 'suspended'
                        ? '#ef4444'
                        : '#ffc107'
                    }}>
                      {selectedCompany.status || 'N/A'}
                    </span>
                  </div>
                  <div><strong>{t('super_admin.plan_label')}:</strong> {selectedCompany.subscription_plan || 'N/A'}</div>
                  <div><strong>{t('super_admin.subscription_status_label')}:</strong> {selectedCompany.subscription_status || 'N/A'}</div>
                  <div><strong>{t('super_admin.created_on')}:</strong> {new Date(selectedCompany.created_at).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                  {selectedCompany.user_count !== undefined && (
                    <div><strong>{t('super_admin.users_count') || 'Nombre d\'utilisateurs'}:</strong> {selectedCompany.user_count}</div>
                  )}
                  {selectedCompany.employee_count !== undefined && (
                    <div><strong>{t('super_admin.employees_count') || 'Nombre d\'employés'}:</strong> {selectedCompany.employee_count}</div>
                  )}
                </div>
              </div>

              {selectedCompany.trial_expires_at && (
                <div style={{
                  background: 'rgba(255, 193, 7, 0.1)',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 193, 7, 0.2)'
                }}>
                  <h3 style={{ color: '#ffffff', marginBottom: '8px', fontSize: '16px', fontWeight: 600 }}>{t('super_admin.trial_period_title')}</h3>
                  <div><strong>{t('super_admin.trial_until')}:</strong> {new Date(selectedCompany.trial_expires_at).toLocaleDateString(dateLocale, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</div>
                  {new Date(selectedCompany.trial_expires_at) > new Date() && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 193, 7, 0.9)' }}>
                      ⏰ {Math.ceil((new Date(selectedCompany.trial_expires_at) - new Date()) / (1000 * 60 * 60 * 24))} {t('super_admin.days_remaining')}
                    </div>
                  )}
                </div>
              )}

              {selectedCompany.stripe_customer_id && (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <h3 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>{t('super_admin.stripe_info')}</h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '12px' }}>
                    <div><strong>{t('super_admin.stripe_customer_id')}:</strong> <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>{selectedCompany.stripe_customer_id}</code></div>
                    {selectedCompany.stripe_subscription_id && (
                      <div><strong>Subscription ID:</strong> <code style={{ background: 'rgba(0,0,0,0.3)', padding: '2px 6px', borderRadius: '4px' }}>{selectedCompany.stripe_subscription_id}</code></div>
                    )}
                    {selectedCompany.current_period_start && (
                      <div><strong>{t('super_admin.period')}:</strong> {new Date(selectedCompany.current_period_start).toLocaleDateString(dateLocale)} - {selectedCompany.current_period_end ? new Date(selectedCompany.current_period_end).toLocaleDateString(dateLocale) : 'N/A'}</div>
                    )}
                  </div>
                </div>
              )}

              {selectedCompany.enabled_features && Array.isArray(selectedCompany.enabled_features) && selectedCompany.enabled_features.length > 0 && (
                <div style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  padding: '16px',
                  borderRadius: '12px',
                  border: '1px solid rgba(255, 255, 255, 0.1)'
                }}>
                  <h3 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>{t('super_admin.modules_enabled')} ({selectedCompany.enabled_features.length})</h3>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedCompany.enabled_features.map((feature, idx) => (
                      <span key={idx} style={{
                        background: 'rgba(16, 185, 129, 0.2)',
                        color: '#10b981',
                        padding: '4px 12px',
                        borderRadius: '8px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}>
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Bouton pour corriger les utilisateurs de l'entreprise */}
              <div style={{
                marginTop: '24px',
                paddingTop: '24px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <button
                  onClick={async () => {
                    if (!window.confirm(t('super_admin.confirm_fix_company_users', { companyName: selectedCompany.name }))) {
                      return;
                    }
                    try {
                      setLoading(true);
                      const token = localStorage.getItem('token');
                      const response = await fetch(`${API_BASE}/api/super-admin/fix-company-users/${selectedCompany.id}`, {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      const data = await response.json();
                      if (response.ok) {
                        setMessage(`✅ ${data.message || t('super_admin.success_fix_company_users')}`);
                        setTimeout(() => setMessage(''), 5000);
                        await loadCompanies();
                      } else {
                        setMessage(`❌ ${t('super_admin.error_fix_company_users')}: ${data.error || ''}`);
                        setTimeout(() => setMessage(''), 5000);
                      }
                    } catch (error) {
                      setMessage(`❌ ${t('super_admin.error_fix_company_users')}: ${error.message || 'Impossible de contacter le serveur'}`);
                      setTimeout(() => setMessage(''), 5000);
                    } finally {
                      setLoading(false);
                    }
                  }}
                  style={{
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    color: '#ffffff',
                    padding: '12px 20px',
                    borderRadius: '12px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '8px',
                    fontSize: '14px',
                    fontWeight: 600,
                    width: '100%',
                    opacity: loading ? 0.7 : 1,
                    transition: 'all 0.3s'
                  }}
                  disabled={loading}
                >
                  <UserCheck size={18} />
                  {t('super_admin.fix_company_users')}
                </button>
                <p style={{
                  marginTop: '8px',
                  fontSize: '12px',
                  color: 'rgba(255, 255, 255, 0.6)',
                  textAlign: 'center'
                }}>
                  {t('super_admin.fix_company_users_description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal Gestion Abonnement */}
      {showSubscriptionModal && selectedSubscription && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}
        onClick={() => setShowSubscriptionModal(false)}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
              borderRadius: '24px',
              maxWidth: '700px',
              width: '100%',
              maxHeight: '90vh',
              display: 'flex',
              flexDirection: 'column',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              overflow: 'visible'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
              padding: '32px 32px 0 32px',
              flexShrink: 0
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#ffffff'
              }}>
                {t('super_admin.subscription_management')} - {selectedSubscription.name}
              </h2>
              <button
                onClick={() => setShowSubscriptionModal(false)}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  color: '#ffffff',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <div style={{ padding: '0 32px 32px 32px', overflowY: 'auto', flex: 1, minHeight: 0 }}>
            {selectedSubscription.subscription_status === 'trial' && (
              <div style={{ marginBottom: '20px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                <button
                  type="button"
                  onClick={(e) => expireTrial(selectedSubscription.id, selectedSubscription.name, e)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(245, 158, 11, 0.9)',
                    border: 'none',
                    color: '#ffffff',
                    padding: '12px 20px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                  title={t('super_admin.expire_trial_test')}
                >
                  <Clock size={18} />
                  {t('super_admin.expire_trial_test')}
                </button>
                <button
                  type="button"
                  onClick={(e) => resetTrial(selectedSubscription.id, selectedSubscription.name, e)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                    background: 'rgba(16, 185, 129, 0.9)',
                    border: 'none',
                    color: '#ffffff',
                    padding: '12px 20px',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 600
                  }}
                  title={t('super_admin.reset_trial_test')}
                >
                  <RefreshCw size={18} />
                  {t('super_admin.reset_trial_test')}
                </button>
                <p style={{ width: '100%', marginTop: '4px', marginBottom: 0, fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                  {language === 'fr' ? 'Expirer : essai en passé. Réinitialiser : 14 jours à partir de maintenant. Pour voir le changement, connectez-vous en tant qu\'utilisateur de cette entreprise.' : 'Expire: sets trial in the past. Reset: 14 days from now. To see the change, log in as a user of this company.'}
                </p>
              </div>
            )}
            <SubscriptionEditForm
              subscription={selectedSubscription}
              t={t}
              onCancel={() => setShowSubscriptionModal(false)}
              onSave={async (formData) => {
                try {
                  const response = await fetch(`${API_BASE}/api/super-admin/companies/${selectedSubscription.id}/subscription`, {
                    method: 'PUT',
                    headers: getAuthHeaders(),
                    body: JSON.stringify({
                      planId: formData.planId,
                      status: formData.status,
                      enabledFeatures: formData.enabledFeatures,
                      attendanceModuleType: formData.attendanceModuleType
                    })
                  });
                  if (response.ok) {
                    setMessage(`✅ ${t('super_admin.success_subscription_updated')}`);
                    await loadSubscriptions();
                    setTimeout(() => {
                      setShowSubscriptionModal(false);
                      setMessage('');
                    }, 3000);
                  } else {
                    const error = await response.json();
                    setMessage(`❌ ${error.error || t('super_admin.error_update_sub')}`);
                    setTimeout(() => setMessage(''), 5000);
                  }
                } catch (error) {
                  console.error('Erreur mise à jour abonnement:', error);
                  setMessage(`❌ ${t('super_admin.error_network')}: ${error.message || t('super_admin.error_contact_server')}`);
                  setTimeout(() => setMessage(''), 5000);
                }
              }}
            />
            </div>
          </div>
        </div>
      )}

      {/* Modal Création Entreprise */}
      {showCreateCompanyModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          padding: '20px'
        }}
        onClick={() => { setShowCreateCompanyModal(false); setCreateCompanyPlan('starter'); }}
        >
          <div
            style={{
              background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
              borderRadius: '24px',
              padding: '32px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px'
            }}>
              <h2 style={{
                fontSize: '24px',
                fontWeight: 700,
                color: '#ffffff'
              }}>
                {t('super_admin.create_company')}
              </h2>
              <button
                onClick={() => { setShowCreateCompanyModal(false); setCreateCompanyPlan('starter'); }}
                style={{
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: 'none',
                  color: '#ffffff',
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const data = {
                  name: formData.get('name')?.trim(),
                  email: formData.get('email')?.trim().toLowerCase(),
                  firstName: formData.get('firstName')?.trim(),
                  lastName: formData.get('lastName')?.trim(),
                  subscriptionPlan: formData.get('subscriptionPlan'),
                  trialDays: parseInt(formData.get('trialDays')) || 14,
                  password: formData.get('password')?.trim() || null
                };
                
                // Validation
                if (!data.name || data.name.length < 2) {
                  setMessage('❌ Le nom de l\'entreprise doit contenir au moins 2 caractères');
                  setTimeout(() => setMessage(''), 5000);
                  return;
                }
                
                if (!data.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
                    setMessage(`❌ ${t('super_admin.error_invalid_email')}`);
                  setTimeout(() => setMessage(''), 5000);
                  return;
                }
                
                if (!data.firstName || data.firstName.length < 2) {
                    setMessage(`❌ ${t('super_admin.error_firstname_min')}`);
                  setTimeout(() => setMessage(''), 5000);
                  return;
                }
                
                if (!data.lastName || data.lastName.length < 2) {
                    setMessage(`❌ ${t('super_admin.error_lastname_min')}`);
                  setTimeout(() => setMessage(''), 5000);
                  return;
                }
                
                if (data.password && data.password.length < 6) {
                    setMessage(`❌ ${t('super_admin.error_password_min')}`);
                  setTimeout(() => setMessage(''), 5000);
                  return;
                }
                
                if (data.trialDays < 0 || data.trialDays > 365) {
                    setMessage(`❌ ${t('super_admin.error_trial_days')}`);
                  setTimeout(() => setMessage(''), 5000);
                  return;
                }
                
                try {
                  setLoading(true);
                  const response = await fetch(`${API_BASE}/api/super-admin/companies`, {
                    method: 'POST',
                    headers: getAuthHeaders(),
                    body: JSON.stringify(data)
                  });
                  
                  if (response.ok) {
                    const result = await response.json();
                    const successMsg = `✅ ${t('super_admin.success_company_created')} "${result.company?.name || data.name}"${result.tempPassword ? `\n\n${t('common.password')}: ${result.tempPassword}\n\n${t('super_admin.temp_password_note')}` : ''}`;
                    setMessage(successMsg);
                    await loadAllData();
                    setTimeout(() => {
                      setShowCreateCompanyModal(false);
                      e.target.reset();
                      setCreateCompanyPlan('starter');
                      setMessage('');
                    }, result.tempPassword ? 15000 : 5000);
                  } else {
                    const error = await response.json();
                    setMessage(`❌ ${error.error || t('super_admin.error_create_company')}`);
                    setTimeout(() => setMessage(''), 5000);
                  }
                } catch (error) {
                  console.error('Erreur création entreprise:', error);
                  setMessage(`❌ Erreur réseau: ${error.message || 'Impossible de contacter le serveur'}`);
                  setTimeout(() => setMessage(''), 5000);
                } finally {
                  setLoading(false);
                }
              }}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}
            >
              <input
                type="text"
                name="name"
                placeholder={t('super_admin.company_name')}
                required
                style={{
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
              <input
                type="email"
                name="email"
                placeholder={t('super_admin.admin_email_placeholder')}
                required
                pattern="[^\s@]+@[^\s@]+\.[^\s@]+"
                style={{
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
              <div style={{ display: 'flex', gap: '12px' }}>
                <input
                  type="text"
                  name="firstName"
                  placeholder="Prénom"
                  required
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
                <input
                  type="text"
                  name="lastName"
                  placeholder="Nom"
                  required
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'rgba(255, 255, 255, 0.1)',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    borderRadius: '12px',
                    color: '#ffffff',
                    fontSize: '14px'
                  }}
                />
              </div>
              <CustomSelect
                name="subscriptionPlan"
                value={createCompanyPlan}
                onChange={(val) => setCreateCompanyPlan(val)}
                placeholder={t('super_admin.plan_starter')}
                options={[
                  { value: 'starter', label: t('super_admin.plan_starter') },
                  { value: 'business', label: t('super_admin.plan_business') },
                  { value: 'enterprise', label: t('super_admin.plan_enterprise') }
                ]}
                style={{
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              />
              <input
                type="number"
                name="trialDays"
                placeholder="Jours d'essai (défaut: 14)"
                defaultValue={14}
                min={0}
                max={365}
                style={{
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
              <input
                type="password"
                name="password"
                placeholder="Mot de passe (optionnel - généré automatiquement si vide, min. 6 caractères)"
                minLength={6}
                style={{
                  padding: '12px',
                  background: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '12px',
                  color: '#ffffff',
                  fontSize: '14px'
                }}
              />
              <button
                type="submit"
                disabled={loading}
                style={{
                  background: loading 
                    ? 'rgba(220, 38, 38, 0.5)' 
                    : 'linear-gradient(135deg, #dc2626, #ef4444)',
                  border: 'none',
                  color: '#ffffff',
                  padding: '14px',
                  borderRadius: '12px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '16px',
                  fontWeight: 600,
                  marginTop: '8px',
                  opacity: loading ? 0.6 : 1
                }}
              >
                {loading ? t('super_admin.saving') : `✅ ${t('super_admin.create_company')}`}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// Composant Modal d'édition d'abonnement
const SubscriptionEditModal = ({ subscription, onClose, onUpdate, onMessage, getAuthHeaders, API_BASE }) => {
  const { t, language } = useTranslation();
  const dateLocale = language === 'fr' ? 'fr-FR' : 'en-GB';
  const [formData, setFormData] = useState({
    planId: subscription.subscription_plan || 'starter',
    status: subscription.subscription_status || 'trial',
    enabledFeatures: subscription.enabled_features || [],
    attendanceModuleType: subscription.attendance_module_type || 'full'
  });
  const [loading, setLoading] = useState(false);
  const [availableFeatures, setAvailableFeatures] = useState([
    'pointage', 'historique', 'calendrier', 'planning', 'ideas', 'communiques', 'dashboard', 
    'employees', 'reports', 'badges', 'formations', 'notes_frais', 
    'conges', 'soldes', 'documents', 'profil', 'audit_photos', 'presence_traceability'
  ]);

  // Features par plan par défaut
  // Aligned with backend planConfig: Starter includes base + Dashboard; Business + Leave/Calendar/Scheduling/Training/Announcements/Ideas; Enterprise + Expense/Live chat/Badges/Performance
  const planFeatures = {
    starter: ['pointage', 'historique', 'documents', 'audit_photos', 'profil', 'reports', 'billing', 'maps', 'gps', 'employees', 'help_faq', 'dashboard'],
    business: ['pointage', 'historique', 'documents', 'audit_photos', 'profil', 'reports', 'billing', 'maps', 'gps', 'employees', 'help_faq', 'conges', 'soldes', 'calendrier', 'planning', 'formations', 'dashboard', 'communiques', 'ideas'],
    enterprise: ['pointage', 'historique', 'documents', 'audit_photos', 'profil', 'reports', 'billing', 'maps', 'gps', 'employees', 'help_faq', 'conges', 'soldes', 'calendrier', 'planning', 'formations', 'dashboard', 'communiques', 'ideas', 'notes_frais', 'live_chat', 'badges', 'performance']
  };

  // Initialiser les features depuis enabled_features ou plan par défaut
  useEffect(() => {
    if (subscription.enabled_features) {
      try {
        const parsed = typeof subscription.enabled_features === 'string' 
          ? JSON.parse(subscription.enabled_features) 
          : subscription.enabled_features;
        if (Array.isArray(parsed) && parsed.length > 0) {
          setFormData(prev => ({
            ...prev,
            enabledFeatures: parsed,
            attendanceModuleType: subscription.attendance_module_type || prev.attendanceModuleType || 'full'
          }));
        } else {
          // Utiliser les features par défaut du plan
          setFormData(prev => ({ 
            ...prev, 
            enabledFeatures: planFeatures[subscription.subscription_plan] || planFeatures.starter,
            attendanceModuleType: subscription.attendance_module_type || prev.attendanceModuleType || 'full'
          }));
        }
      } catch (err) {
        setFormData(prev => ({ 
          ...prev, 
          enabledFeatures: planFeatures[subscription.subscription_plan] || planFeatures.starter,
          attendanceModuleType: subscription.attendance_module_type || prev.attendanceModuleType || 'full'
        }));
      }
    } else {
      // Utiliser les features par défaut du plan
      setFormData(prev => ({ 
        ...prev, 
        enabledFeatures: planFeatures[subscription.subscription_plan] || planFeatures.starter,
        attendanceModuleType: subscription.attendance_module_type || prev.attendanceModuleType || 'full'
      }));
    }
  }, [subscription]);

  // Appliquer les features par défaut quand le plan change
  const handlePlanChange = (newPlan) => {
    setFormData(prev => ({
      ...prev,
      planId: newPlan,
      enabledFeatures: planFeatures[newPlan] || planFeatures.starter
    }));
  };

  const toggleFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      enabledFeatures: prev.enabledFeatures.includes(feature)
        ? prev.enabledFeatures.filter(f => f !== feature)
        : [...prev.enabledFeatures, feature]
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/super-admin/companies/${subscription.id}/subscription`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          planId: formData.planId,
          status: formData.status,
          enabledFeatures: formData.enabledFeatures,
          attendanceModuleType: formData.attendanceModuleType
        })
      });

      if (response.ok) {
        const data = await response.json();
        onMessage(`✅ ${t('super_admin.success_subscription_updated')}`);
        await onUpdate();
      } else {
        const error = await response.json();
        onMessage(`❌ ${t('super_admin.error_update')}: ${error.error || ''}`);
      }
    } catch (error) {
      onMessage(`❌ ${t('super_admin.error_update')}`);
    } finally {
      setLoading(false);
    }
  };

  const getFeatureLabel = (feature) => {
    const key = `access_denied.feature_${feature}`;
    const translated = t(key);
    return translated !== key ? translated : feature;
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2000,
      padding: '20px'
    }}
    onClick={onClose}
  >
      <div
        style={{
          background: 'linear-gradient(180deg, #0a0e27 0%, #1a1f3a 100%)',
          borderRadius: '24px',
          maxWidth: '800px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          overflow: 'visible'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
          padding: '32px 32px 0 32px',
          flexShrink: 0
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: 700,
            color: '#ffffff'
          }}>
            {t('super_admin.subscription_management_title')} - {subscription.name}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              color: '#ffffff',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '24px',
          color: 'rgba(255, 255, 255, 0.8)',
          fontSize: '14px',
          padding: '0 32px 32px 32px',
          overflowY: 'auto',
          flex: 1,
          minHeight: 0
        }}>
          {/* Informations Stripe */}
          <div style={{
            background: 'rgba(255, 255, 255, 0.05)',
            padding: '16px',
            borderRadius: '12px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>{t('super_admin.stripe_info')}</h3>
            {subscription.stripe_customer_id && (
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('super_admin.stripe_customer_id')}:</strong> {subscription.stripe_customer_id}
              </div>
            )}
            {subscription.stripe_subscription_id && (
              <div style={{ marginBottom: '8px' }}>
                <strong>{t('super_admin.stripe_subscription_id')}:</strong> {subscription.stripe_subscription_id}
              </div>
            )}
            {subscription.current_period_start && (
              <div>
                <strong>{t('super_admin.period')}:</strong> {new Date(subscription.current_period_start).toLocaleDateString('fr-FR')} - {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString('fr-FR') : 'N/A'}
              </div>
            )}
          </div>

          {/* Configuration Plan */}
          <div style={{ position: 'relative', zIndex: 10 }}>
            <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontWeight: 600 }}>
              {t('super_admin.subscription_plan')}
            </label>
            <CustomSelect
              name="planId"
              value={formData.planId}
              onChange={(val) => handlePlanChange(val)}
              placeholder={t('super_admin.plan_starter')}
              options={[
                { value: 'starter', label: t('super_admin.plan_starter') },
                { value: 'business', label: t('super_admin.plan_business') },
                { value: 'enterprise', label: t('super_admin.plan_enterprise') }
              ]}
              style={{
                width: '100%',
                minHeight: '44px',
                padding: '12px 36px 12px 12px',
                background: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Configuration Statut */}
          <div style={{ position: 'relative', zIndex: 10 }}>
            <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontWeight: 600 }}>
              {t('super_admin.subscription_status')}
            </label>
            <CustomSelect
              name="status"
              value={formData.status}
              onChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
              placeholder={t('super_admin.status_trial_free')}
              options={[
                { value: 'trial', label: t('super_admin.status_trial_free') },
                { value: 'active', label: t('super_admin.status_active') },
                { value: 'suspended', label: t('super_admin.status_suspended') },
                { value: 'cancelled', label: t('super_admin.status_cancelled') }
              ]}
              style={{
                width: '100%',
                minHeight: '44px',
                padding: '12px 36px 12px 12px',
                background: 'rgba(255, 255, 255, 0.12)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '8px',
                color: '#ffffff',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            />
          </div>

          {/* Configuration Features */}
          <div>
            <label style={{ display: 'block', color: '#ffffff', marginBottom: '12px', fontWeight: 600 }}>
              {t('super_admin.modules_enabled')}
            </label>
            <div style={{
              background: 'rgba(255, 255, 255, 0.05)',
              padding: '16px',
              borderRadius: '12px',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              maxHeight: '300px',
              overflowY: 'auto',
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
              gap: '12px'
            }}>
              {availableFeatures.map(feature => (
                <label
                  key={feature}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    padding: '8px',
                    borderRadius: '8px',
                    background: formData.enabledFeatures.includes(feature) 
                      ? 'rgba(16, 185, 129, 0.2)' 
                      : 'rgba(255, 255, 255, 0.05)',
                    border: formData.enabledFeatures.includes(feature)
                      ? '1px solid rgba(16, 185, 129, 0.3)'
                      : '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.2s'
                  }}
                >
                  <input
                    type="checkbox"
                    checked={formData.enabledFeatures.includes(feature)}
                    onChange={() => toggleFeature(feature)}
                    style={{
                      width: '18px',
                      height: '18px',
                      cursor: 'pointer'
                    }}
                  />
                  <span style={{ fontSize: '13px' }}>{getFeatureLabel(feature)}</span>
                </label>
              ))}
            </div>
            <p style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
              💡 Les features sont automatiquement configurées selon le plan, mais vous pouvez les modifier manuellement.
            </p>
          </div>

          {formData.enabledFeatures.includes('pointage') && (
            <div style={{ position: 'relative', zIndex: 10 }}>
              <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontWeight: 600 }}>
                {t('super_admin.attendance_module_label')}
              </label>
              <CustomSelect
                name="attendanceModuleType"
                value={formData.attendanceModuleType || 'full'}
                onChange={(val) => setFormData(prev => ({ ...prev, attendanceModuleType: val }))}
                options={[
                  { value: 'full', label: t('super_admin.attendance_module_full') },
                  { value: 'simplified', label: t('super_admin.attendance_module_simplified') },
                  { value: 'gps_only', label: t('super_admin.attendance_module_gps_only') },
                  { value: 'ultra_simple', label: t('super_admin.attendance_module_ultra') }
                ]}
                style={{
                  width: '100%',
                  minHeight: '44px',
                  padding: '12px 36px 12px 12px',
                  background: 'rgba(255, 255, 255, 0.12)',
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                  borderRadius: '8px',
                  color: '#ffffff',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              />
              <p style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.55)' }}>
                {t('super_admin.attendance_module_hint')}
              </p>
            </div>
          )}

          {/* Boutons d'action */}
          <div style={{
            display: 'flex',
            gap: '12px',
            marginTop: '20px'
          }}>
            <button
              onClick={handleSave}
              disabled={loading}
              style={{
                flex: 1,
                background: loading ? 'rgba(16, 185, 129, 0.5)' : 'linear-gradient(135deg, #10b981, #059669)',
                border: 'none',
                color: '#ffffff',
                padding: '14px 24px',
                borderRadius: '12px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: 600,
                transition: 'all 0.3s'
              }}
            >
              {loading ? t('super_admin.saving') : t('super_admin.save_changes')}
            </button>
            <button
              onClick={onClose}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                padding: '14px 24px',
                borderRadius: '12px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: 600
              }}
            >
              {t('super_admin.cancel')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Composant Formulaire d'édition d'abonnement (utilisé dans le modal)
const SubscriptionEditForm = ({ subscription, onSave, onCancel, t }) => {
  const { language } = useTranslation();
  const dateLocale = language === 'fr' ? 'fr-FR' : 'en-GB';
  const normPlan = (p) => (p && String(p).toLowerCase().trim()) || 'starter';
  const [formData, setFormData] = useState({
    planId: normPlan(subscription.subscription_plan),
    status: subscription.subscription_status || 'trial',
    enabledFeatures: [],
    attendanceModuleType: subscription.attendance_module_type || 'full'
  });
  const [loading, setLoading] = useState(false);

  // Features par plan par défaut
  // Aligned with backend planConfig (same as above)
  const planFeatures = {
    starter: ['pointage', 'historique', 'documents', 'audit_photos', 'profil', 'reports', 'billing', 'maps', 'gps', 'employees', 'help_faq', 'dashboard'],
    business: ['pointage', 'historique', 'documents', 'audit_photos', 'profil', 'reports', 'billing', 'maps', 'gps', 'employees', 'help_faq', 'conges', 'soldes', 'calendrier', 'planning', 'formations', 'dashboard', 'communiques', 'ideas'],
    enterprise: ['pointage', 'historique', 'documents', 'audit_photos', 'profil', 'reports', 'billing', 'maps', 'gps', 'employees', 'help_faq', 'conges', 'soldes', 'calendrier', 'planning', 'formations', 'dashboard', 'communiques', 'ideas', 'notes_frais', 'live_chat', 'badges', 'performance']
  };

  const availableFeatures = [
    'pointage', 'historique', 'documents', 'audit_photos', 'profil', 'reports', 'billing', 'maps', 'gps', 'employees', 'help_faq',
    'conges', 'soldes', 'calendrier', 'planning', 'formations', 'dashboard', 'communiques', 'ideas',
    'notes_frais', 'live_chat', 'badges', 'performance', 'presence_traceability'
  ];

  const planKey = normPlan(subscription.subscription_plan);

  // Initialiser plan + features quand subscription change (ou au mount)
  useEffect(() => {
    const plan = planKey;
    if (subscription.enabled_features) {
      try {
        const parsed = typeof subscription.enabled_features === 'string' 
          ? JSON.parse(subscription.enabled_features) 
          : subscription.enabled_features;
        if (Array.isArray(parsed) && parsed.length > 0) {
          setFormData(prev => ({
            ...prev,
            planId: plan,
            enabledFeatures: parsed,
            attendanceModuleType: subscription.attendance_module_type || prev.attendanceModuleType || 'full'
          }));
        } else {
          setFormData(prev => ({ 
            ...prev, 
            planId: plan,
            enabledFeatures: planFeatures[plan] || planFeatures.starter,
            attendanceModuleType: subscription.attendance_module_type || prev.attendanceModuleType || 'full'
          }));
        }
      } catch (err) {
        setFormData(prev => ({ 
          ...prev, 
          planId: plan,
          enabledFeatures: planFeatures[plan] || planFeatures.starter,
          attendanceModuleType: subscription.attendance_module_type || prev.attendanceModuleType || 'full'
        }));
      }
    } else {
      setFormData(prev => ({ 
        ...prev, 
        planId: plan,
        enabledFeatures: planFeatures[plan] || planFeatures.starter,
        attendanceModuleType: subscription.attendance_module_type || prev.attendanceModuleType || 'full'
      }));
    }
  }, [subscription.id, subscription.subscription_plan, subscription.enabled_features, subscription.attendance_module_type]);

  const handlePlanChange = (newPlan) => {
    const plan = (newPlan && String(newPlan).toLowerCase().trim()) || 'starter';
    setFormData(prev => ({
      ...prev,
      planId: plan,
      enabledFeatures: planFeatures[plan] || planFeatures.starter
    }));
  };

  const toggleFeature = (feature) => {
    setFormData(prev => ({
      ...prev,
      enabledFeatures: prev.enabledFeatures.includes(feature)
        ? prev.enabledFeatures.filter(f => f !== feature)
        : [...prev.enabledFeatures, feature]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave({
        ...formData,
        planId: (formData.planId && String(formData.planId).toLowerCase().trim()) || 'starter'
      });
    } finally {
      setLoading(false);
    }
  };

  const getFeatureLabel = (feature) => {
    if (!t) return feature;
    const key = `access_denied.feature_${feature}`;
    const translated = t(key);
    return translated !== key ? translated : feature;
  };

  return (
    <form onSubmit={handleSubmit} style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '24px',
      color: 'rgba(255, 255, 255, 0.8)',
      fontSize: '14px'
    }}>
      {/* Informations Stripe */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.05)',
        padding: '16px',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        <h3 style={{ color: '#ffffff', marginBottom: '12px', fontSize: '16px', fontWeight: 600 }}>{t('super_admin.stripe_info')}</h3>
        {subscription.stripe_customer_id && (
          <div style={{ marginBottom: '8px' }}>
            <strong>{t('super_admin.stripe_customer_id')}:</strong> {subscription.stripe_customer_id}
          </div>
        )}
        {subscription.stripe_subscription_id && (
          <div style={{ marginBottom: '8px' }}>
            <strong>{t('super_admin.stripe_subscription_id')}:</strong> {subscription.stripe_subscription_id}
          </div>
        )}
        {subscription.current_period_start && (
          <div>
            <strong>{t('super_admin.period')}:</strong> {new Date(subscription.current_period_start).toLocaleDateString(dateLocale)} - {subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString(dateLocale) : 'N/A'}
          </div>
        )}
      </div>

      {/* Plan */}
      <div style={{ position: 'relative', zIndex: 10 }}>
        <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontWeight: 600 }}>
          {t('super_admin.subscription_plan')}
        </label>
        <CustomSelect
          name="planId"
          value={formData.planId}
          onChange={(val) => handlePlanChange(val)}
          placeholder={t('super_admin.plan_starter')}
          options={[
            { value: 'starter', label: t('super_admin.plan_starter') },
            { value: 'business', label: t('super_admin.plan_business') },
            { value: 'enterprise', label: t('super_admin.plan_enterprise') }
          ]}
          style={{
            width: '100%',
            minHeight: '44px',
            padding: '12px 36px 12px 12px',
            background: 'rgba(255, 255, 255, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            color: '#ffffff',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        />
      </div>

      {/* Statut */}
      <div style={{ position: 'relative', zIndex: 10 }}>
        <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontWeight: 600 }}>
          {t('super_admin.subscription_status')}
        </label>
        <CustomSelect
          name="status"
          value={formData.status}
          onChange={(val) => setFormData(prev => ({ ...prev, status: val }))}
          placeholder={t('super_admin.status_trial_free')}
          options={[
            { value: 'trial', label: t('super_admin.status_trial_free') },
            { value: 'active', label: t('super_admin.status_active') },
            { value: 'suspended', label: t('super_admin.status_suspended') },
            { value: 'cancelled', label: t('super_admin.status_cancelled') }
          ]}
          style={{
            width: '100%',
            minHeight: '44px',
            padding: '12px 36px 12px 12px',
            background: 'rgba(255, 255, 255, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            borderRadius: '8px',
            color: '#ffffff',
            fontSize: '14px',
            cursor: 'pointer'
          }}
        />
      </div>

      {/* Features */}
      <div>
        <label style={{ display: 'block', color: '#ffffff', marginBottom: '12px', fontWeight: 600 }}>
          {t('super_admin.modules_enabled')}
        </label>
        <div style={{
          background: 'rgba(255, 255, 255, 0.05)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          maxHeight: '300px',
          overflowY: 'auto',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
          gap: '12px'
        }}>
          {availableFeatures.map(feature => (
            <label
              key={feature}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '8px',
                background: formData.enabledFeatures.includes(feature) 
                  ? 'rgba(16, 185, 129, 0.2)' 
                  : 'rgba(255, 255, 255, 0.05)',
                border: formData.enabledFeatures.includes(feature)
                  ? '1px solid rgba(16, 185, 129, 0.3)'
                  : '1px solid rgba(255, 255, 255, 0.1)',
                transition: 'all 0.2s'
              }}
            >
              <input
                type="checkbox"
                checked={formData.enabledFeatures.includes(feature)}
                onChange={() => toggleFeature(feature)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span style={{ fontSize: '13px' }}>{getFeatureLabel(feature)}</span>
            </label>
          ))}
        </div>
        <p style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
          {t ? t('super_admin.features_auto_config') : '💡 Les features sont automatiquement configurées selon le plan, mais vous pouvez les modifier manuellement.'}
        </p>
      </div>

      {formData.enabledFeatures.includes('pointage') && (
        <div style={{ position: 'relative', zIndex: 10 }}>
          <label style={{ display: 'block', color: '#ffffff', marginBottom: '8px', fontWeight: 600 }}>
            {t('super_admin.attendance_module_label')}
          </label>
          <CustomSelect
            name="attendanceModuleType"
            value={formData.attendanceModuleType || 'full'}
            onChange={(val) => setFormData(prev => ({ ...prev, attendanceModuleType: val }))}
            options={[
              { value: 'full', label: t('super_admin.attendance_module_full') },
              { value: 'simplified', label: t('super_admin.attendance_module_simplified') },
              { value: 'gps_only', label: t('super_admin.attendance_module_gps_only') },
              { value: 'ultra_simple', label: t('super_admin.attendance_module_ultra') }
            ]}
            style={{
              width: '100%',
              minHeight: '44px',
              padding: '12px 36px 12px 12px',
              background: 'rgba(255, 255, 255, 0.12)',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              borderRadius: '8px',
              color: '#ffffff',
              fontSize: '14px',
              cursor: 'pointer'
            }}
          />
          <p style={{ marginTop: '8px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.55)' }}>
            {t('super_admin.attendance_module_hint')}
          </p>
        </div>
      )}

      {/* Boutons */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginTop: '20px'
      }}>
        <button
          type="submit"
          disabled={loading}
          style={{
            flex: 1,
            background: loading ? 'rgba(16, 185, 129, 0.5)' : 'linear-gradient(135deg, #10b981, #059669)',
            border: 'none',
            color: '#ffffff',
            padding: '14px 24px',
            borderRadius: '12px',
            cursor: loading ? 'not-allowed' : 'pointer',
            fontSize: '16px',
            fontWeight: 600,
            transition: 'all 0.3s'
          }}
        >
          {loading ? t('super_admin.saving') : t('super_admin.save_changes')}
        </button>
        <button
          type="button"
          onClick={onCancel || (() => window.location.reload())}
          style={{
            background: 'rgba(255, 255, 255, 0.1)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            color: '#ffffff',
            padding: '14px 24px',
            borderRadius: '12px',
            cursor: 'pointer',
            fontSize: '16px',
            fontWeight: 600
          }}
        >
          {t('super_admin.cancel')}
        </button>
      </div>
    </form>
  );
};

export default SuperAdminPanel;





